import { UploadManager } from '@bytescale/sdk';

import { getMediaUploadPath } from '@/config/uploadConfig';
import { apiKey } from '@/config/utils';
import { generateUUID } from '@/utils';

import type {
  AttachmentTarget, ChatAttachment, FormAiDescriptor, FormAiField,
} from './types';

const uploadManager = new UploadManager({ apiKey });

export const ATTACHMENT_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'application/pdf', 'text/plain'];
export const ATTACHMENT_MAX_BYTES = 4 * 1024 * 1024;
export const ATTACHMENTS_MAX = 3;

export const readAsBase64 = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(blob);
});

/** Plik z komputera: do Biblioteki mediów (ten sam folder co uploady formularzy) + base64 dla modelu. */
export const attachmentFromFile = async (file: File, organizationId: string | null | undefined): Promise<ChatAttachment> => {
  const { folderPath } = getMediaUploadPath(organizationId);
  const ext = file.name.match(/\.[^.]+$/)?.[0] ?? '';
  const base = file.name.replace(/\.[^.]+$/, '') || 'file';
  const result = await uploadManager.upload({
    data: file,
    mime: file.type || undefined,
    originalFileName: file.name,
    path: { folderPath, fileName: `${base}-${generateUUID()}${ext}` },
  });
  return {
    name: file.name, type: file.type, size: file.size, data: await readAsBase64(file), url: result.fileUrl,
  };
};

/**
 * Okładka wygenerowana przez asystenta: data URL z podglądu → Bytescale, ten sam folder Biblioteki
 * mediów i ta sama konwencja nazw co ręczny upload (`ImageUpload`), więc dalej działa jak każdy obraz.
 */
export const uploadCoverImage = async (dataUrl: string, organizationId: string | null | undefined, formId: string): Promise<string> => {
  const { folderPath } = getMediaUploadPath(organizationId);
  const blob = await (await fetch(dataUrl)).blob();
  const originalFileName = `ai-cover-${formId}.jpg`;
  const result = await uploadManager.upload({
    data: blob,
    mime: 'image/jpeg',
    originalFileName,
    path: { folderPath, fileName: `ai-cover-${formId}-${generateUUID()}.jpg` },
  });
  return result.fileUrl;
};

const MIME_BY_EXT: Record<string, string> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif', pdf: 'application/pdf', txt: 'text/plain',
};

/** Plik z Biblioteki mediów: pobieramy bajty do kontekstu modelu, URL zostaje do wpisania w formularz. */
export const attachmentFromUrl = async (url: string): Promise<ChatAttachment> => {
  const name = decodeURIComponent(url.split('?')[0].split('/').pop() || 'plik');
  const ext = name.split('.').pop()?.toLowerCase() || '';
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fetch ${res.status}`);
  const blob = await res.blob();
  const type = blob.type && blob.type !== 'application/octet-stream' ? blob.type : (MIME_BY_EXT[ext] || 'application/octet-stream');
  return {
    name, type, size: blob.size, data: await readAsBase64(blob), url,
  };
};

const isFilled = (v: unknown): boolean => (Array.isArray(v) ? v.length > 0 : typeof v === 'string' && v.trim().length > 0);

/**
 * Załączniki → pola formularza (wspólna reguła dla wszystkich formularzy):
 * 1. jawne wskazania z odpowiedzi modelu (`attachmentTargets`, gdy użytkownik napisał, który plik jest czym),
 * 2. reszta w KOLEJNOŚCI załączenia: obrazy do kolejnych pustych pól `image` (pole `multiple`, np. galeria,
 *    przyjmuje wszystkie kolejne obrazy), PDF do kolejnych pustych pól `file`.
 * Pola już wypełnione w formularzu (`current`) są pomijane, chyba że wskazano je jawnie.
 * Zwraca mapę nazwa pola → URL (lub lista URL-i dla pól `multiple`).
 */
export const assignAttachmentsToFields = (
  descriptor: FormAiDescriptor,
  attachments: ChatAttachment[],
  targets: AttachmentTarget[] = [],
  current: Record<string, unknown> = {},
): Record<string, string | string[]> => {
  const byName = new Map(descriptor.fields.filter((f) => (f.kind === 'image' || f.kind === 'file') && !f.readOnly).map((f) => [f.name, f]));
  const out: Record<string, string | string[]> = {};
  const used = new Set<number>();

  const put = (field: FormAiField, url: string) => {
    if (field.multiple) {
      const base = Array.isArray(out[field.name]) ? (out[field.name] as string[]) : (Array.isArray(current[field.name]) ? [...(current[field.name] as string[])] : []);
      if (!base.includes(url)) base.push(url);
      out[field.name] = base;
    } else {
      out[field.name] = url;
    }
  };

  // 1. Jawne wskazania użytkownika.
  for (const t of targets) {
    const a = attachments[t.index - 1];
    const field = byName.get(t.field);
    if (!a?.url || !field || used.has(t.index - 1)) continue;
    const isImage = a.type.startsWith('image/');
    if ((field.kind === 'image') !== isImage) continue;
    put(field, a.url);
    used.add(t.index - 1);
  }

  // 2. Reszta w kolejności do wolnych pól. Pole `multiple` (galeria) pochłania WSZYSTKIE kolejne obrazy:
  //    pola obrazów za nim dostają plik tylko przez jawne wskazanie.
  const imageQueue = descriptor.fields.filter((f) => f.kind === 'image' && !f.readOnly && (f.multiple || (!(f.name in out) && !isFilled(current[f.name]))));
  const fileQueue = descriptor.fields.filter((f) => f.kind === 'file' && !f.readOnly && !(f.name in out) && !isFilled(current[f.name]));
  let imgPos = 0;
  let filePos = 0;
  attachments.forEach((a, i) => {
    if (used.has(i) || !a.url) return;
    if (a.type.startsWith('image/')) {
      const next = imageQueue[imgPos];
      if (!next) return;
      put(next, a.url);
      if (!next.multiple) imgPos += 1;
    } else if (a.type === 'application/pdf') {
      const next = fileQueue[filePos];
      if (!next) return;
      put(next, a.url);
      filePos += 1;
    }
  });
  return out;
};
